import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import InputAdornment from '@mui/material/InputAdornment';
import { useColorScheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch } from 'react-redux';

import useFormFieldValue from '@gef-ui/form-core/hooks/state/useFormFieldValue';
import Autocomplete from '@gef-ui/form-material/Autocomplete';

import { subheaders } from 'features/common/Subheader/constants';
import { setVisibleState } from 'features/common/Subheader/slice';
import { saveSidebarFormValuesCache, setProcess } from 'features/views/campaignManagement/contactReleasing/actions';
import { resetOtherSidebarFields } from 'features/views/campaignManagement/sidebar/actions';

const SidebarAutoComplete = ({ options, fieldName, label, icon, process, ...rest }) => {
	const dispatch = useDispatch();
	const { mode: appTheme } = useColorScheme();

	const [focused, setFocused] = useState(false);
	const fieldValue = useFormFieldValue({ fieldName });

	const handleOnFocus = useCallback(() => setFocused(true), []);
	const handleOnBlur = useCallback(() => setFocused(false), []);

	const renderInput = useCallback(
		(params) => {
			const startAdornment = (
				<InputAdornment position="start" sx={{ ml: 1, color: 'info.main' }}>
					{icon}
				</InputAdornment>
			);

			return (
				<TextField
					{...params}
					label={label}
					fullWidth
					slotProps={{
						input: {
							...params.InputProps,
							startAdornment,
						},

						inputLabel: {
							shrink: focused || !isNilOrEmpty(fieldValue),
							style: { marginLeft: focused || !isNilOrEmpty(fieldValue) ? 0 : 30 },
						},
					}}
					onFocus={handleOnFocus}
					onBlur={handleOnBlur}
				/>
			);
		},
		[fieldValue, focused, label, icon, handleOnFocus, handleOnBlur],
	);

	const handleOnChange = useCallback(
		(e, value) => {
			dispatch(setVisibleState({ id: subheaders.campaignManagementSidebarManualAssign, visible: false }));
			dispatch(resetOtherSidebarFields(process));
			dispatch(saveSidebarFormValuesCache());
			if (value) {
				dispatch(setProcess({ process }));
			} else {
				dispatch(setProcess({ process: null }));
			}
		},
		[dispatch, process],
	);

	return (
		<Autocomplete
			size="small"
			fieldName={fieldName}
			label={label}
			options={options}
			labelPropName="shortDescription"
			renderInput={renderInput}
			onChange={handleOnChange}
			sx={{
				cursor: 'pointer',
				backgroundColor: (theme) =>
					rest.disabled
						? 'default'
						: appTheme === 'light'
						  ? !isNilOrEmpty(fieldValue)
								? 'primary.shades.8p'
								: theme.palette.common.white
						  : !isNilOrEmpty(fieldValue)
						    ? 'primary.shades.30p'
						    : theme.palette.background[300],
			}}
			{...rest}
		/>
	);
};

SidebarAutoComplete.propTypes = {
	fieldName: PropTypes.string,
	icon: PropTypes.element,
	label: PropTypes.string,
	options: PropTypes.array,
	process: PropTypes.string,
};

export default SidebarAutoComplete;
